/**
 * TanStack Query hook for public site configuration
 * 
 * Social media URLs are configured by admins and change rarely, so we cache for 1 hour.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';

export interface SocialLinks {
  social_facebook_url: string;
  social_x_url: string;
  social_instagram_url: string;
  social_youtube_url: string;
  social_tiktok_url: string;
}

const ONE_HOUR = 60 * 60 * 1000;

/**
 * Fetch social media link URLs from site configuration
 */
export function useSocialLinksQuery() {
  return useQuery({
    queryKey: ['site-config', 'social-links'],
    queryFn: async (): Promise<SocialLinks> => {
      const response = await apiClient.get<{ success: boolean; links: SocialLinks }>(
        '/site-config/public/social'
      );

      if (response?.success && response.links) {
        return response.links;
      }
      return {
        social_facebook_url: '',
        social_x_url: '',
        social_instagram_url: '',
        social_youtube_url: '',
        social_tiktok_url: '',
      };
    },
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR * 2,
  });
}

export interface PurchaseLimits {
  max_sc_balance: number | null;
}

/**
 * Fetch purchase limit configuration from site configuration.
 * Used to check if the user's SC balance exceeds the maximum allowed for purchasing.
 * Only fetches when user is authenticated (purchase limits only matter for logged-in users).
 */
export function usePurchaseLimitsQuery() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['site-config', 'purchase-limits'],
    queryFn: async (): Promise<PurchaseLimits> => {
      const response = await apiClient.get<{ success: boolean; limits: PurchaseLimits }>(
        '/site-config/public/purchase-limits'
      );

      if (response?.success && response.limits) {
        return response.limits;
      }
      return { max_sc_balance: null };
    },
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR * 2,
    enabled: !!user,
  });
}
